/**
 * adjacencyTranslations.js
 * --------------------------------------------------------------------------
 * Translation overrides for the adjacency justifications in skillAdjacency.js.
 *
 * The adjacency map ships with EN justifications inline (so the matcher
 * works without any i18n setup). When the UI renders an adjacency, it
 * looks up a translation here keyed by `${targetSkillId}.${viaSkillId}`.
 * If a translation exists for the active language, use it. Else fall back
 * to the inline English justification from skillAdjacency.js.
 *
 * This lets us ship translations for the most common adjacencies tonight
 * (the ones that fire on Pascal's senior-sales CV) without translating
 * all 79 edges in one go.
 */

const TRANSLATIONS = {
  de: {
    // Sales family, these fire most often on Pascal's CV
    'csuite.stakeholder':         'Stakeholder-Management auf Senior-Ebene überschneidet sich direkt mit C-Level-Ansprache.',
    'csuite.communication':       'Executive Communication ist ein Kernelement der C-Level-Glaubwürdigkeit.',
    'csuite.enterpriseSales':     'Enterprise-Sales-Senior-Zyklen erfordern routinemäßig C-Level-Sponsoring.',
    'pipelineManagement.territoryPlanning': 'Vertriebsgebietsplanung schafft die Struktur, in der Pipeline-Management operiert.',
    'pipelineManagement.forecastAccuracy':  'Forecasting und Pipeline-Management teilen die gleiche Datendisziplin.',
    'pipelineManagement.crm':     'CRM-Beherrschung ist die operative Grundlage des Pipeline-Managements.',
    'enterpriseSales.accountManagement': 'Account-Management großer Kunden ist Enterprise-Vertrieb in der Expansionsphase.',
    'enterpriseSales.csuite':     'Enterprise-Deals erfordern Senior-Stakeholder-Kompetenz.',
    'accountManagement.enterpriseSales': 'Enterprise-Vertrieb übernimmt nach Abschluss typischerweise das Account-Wachstum.',
    'accountManagement.stakeholder': 'Account-Management ist Multi-Stakeholder-Beziehungsarbeit.',
    'negotiation.enterpriseSales': 'Enterprise-Vertrieb bedeutet anhaltende Verhandlungen mit Procurement.',
    'negotiation.csuite':         'C-Level-Ansprache umfasst die Verhandlung strategischer Konditionen.',
    'forecastAccuracy.pipelineManagement': 'Forecasting und Pipeline-Management teilen die gleiche Datendisziplin.',
    'forecastAccuracy.analytics': 'Forecasting basiert auf quantitativ-analytischer Kompetenz.',
    'enablement.mentoring':       'Sales Enablement ist strukturiertes Peer-Mentoring im Maßstab.',
    'enablement.change':          'Enablement-Programme sind Change Management für Vertriebsmitarbeiter.',
    'enablement.communication':   'Enablement erfordert starke Inhalts- und Vermittlungsfähigkeiten.',
    'channelPartner.enterpriseSales':   'Channel-Vertrieb ist Enterprise-Vertrieb über einen Drittanbieter.',
    'channelPartner.accountManagement': 'Partner-Beziehungen sind verwaltete Accounts.',
    'channelPartner.goToMarket':  'Channel-Strategie ist eine Go-to-Market-Entscheidung.',
    'goToMarket.strategicPlanning': 'Go-to-Market-Planung ist eine strategische Planungsspezialität.',
    'goToMarket.demandGen':       'Demand Generation ist ein primärer GTM-Hebel.',
    'strategicPlanning.goToMarket': 'GTM-Planung ist eine Form strategischer Planung.',
    'strategicPlanning.pAndL':    'P&L-Verantwortung erfordert strategische Planungsfähigkeit.',
    'pAndL.strategicPlanning':    'P&L-Verantwortung erfordert strategische Planungskompetenz.',
    'pAndL.forecastAccuracy':     'P&L-Verantwortung erfordert Forecasting.',
    'teamLeadership.mentoring':   'Mentoring ist eine tägliche operative Komponente von Führung.',
    'teamLeadership.change':      'Teams durch Veränderungen zu führen ist Führungsarbeit.',
    'teamLeadership.communication': 'Executive Communication ist eine Führungsanforderung.',
    'crm.pipelineManagement':     'Pipeline-Management erfordert in der Praxis CRM-Beherrschung.',

    // HR family
    'compStrategy.payEquity':     'Pay-Equity-Arbeit ist eine Spezialisierung der Vergütungsstrategie.',
    'compStrategy.hrAnalytics':   'Vergütungsstrategie-Entscheidungen basieren auf HR-Analytics.',
    'payEquity.compStrategy':     'Pay Equity ist die equity-fokussierte Teilmenge der Vergütungsstrategie.',
    'payEquity.gdprHr':           'Pay Equity überschneidet sich direkt mit arbeitsrechtlicher Compliance.',
    'payEquity.hrAnalytics':      'Pay Equity ist im Kern eine Analytics-Disziplin.',
    'talentMgmt.orgDesign':       'Talent-Management-Entscheidungen leben innerhalb von Organisationsdesign-Entscheidungen.',
    'talentMgmt.hrAnalytics':     'Modernes Talent Management ist datengetrieben.',
    'orgDesign.talentMgmt':       'Organisationsdesign setzt die Talent-Management-Strategie um.',
    'orgDesign.change':           'Organisationsdesign-Veränderungen sind Change-Management-Programme.',
    'orgDesign.strategicPlanning': 'Organisationsdesign folgt aus strategischer Planung.',
    'employeeRelations.gdprHr':   'Mitarbeiterbeziehungen sind intensiv in der Anwendung von Arbeitsrecht.',
    'employeeRelations.change':   'Bedeutende ER-Arbeit fällt während organisatorischer Veränderung an.',
    'hrAnalytics.analytics':      'HR Analytics ist Datenanalyse angewandt auf Personaldaten.',
    'hrAnalytics.sql':            'SQL ist das Hauptwerkzeug von HR Analytics in den meisten Organisationen.',
    'hrAnalytics.compStrategy':   'Vergütungsstrategie-Entscheidungen werden durch HR Analytics erzeugt.',
    'workdayHris.projectMgmt':    'HRIS-Rollouts sind große Projektmanagement-Übungen.',
    'gdprHr.employeeRelations':   'ER wendet häufig arbeitsrechtliche Prinzipien an.',
    'gdprHr.payEquity':           'Pay-Equity-Arbeit berührt DSGVO-Pflichten.',

    // Product / general business
    'productMgmt.roadmap':        'Roadmap-Planung ist eine Kerndisziplin des Produktmanagements.',
    'productMgmt.stakeholder':    'Produktmanagement ist überwiegend Stakeholder-Management.',
    'productMgmt.analytics':      'Modernes Produktmanagement ist metrikgetrieben.',
    'roadmap.productMgmt':        'Roadmap-Planung ist Produktmanagement-Arbeit.',
    'roadmap.strategicPlanning':  'Roadmaps sind strategische Pläne für Produkte.',
    'userResearch.productMgmt':   'PMs führen routinemäßig User Research durch oder beauftragen sie.',
    'userResearch.analytics':     'Quantitative Analytics ist die Hälfte von User Research.',
    'stakeholder.csuite':         'Stakeholder-Management auf Executive-Ebene IST C-Level-Ansprache.',
    'stakeholder.communication':  'Stakeholder-Management hängt von exzellenter Executive Communication ab.',
    'stakeholder.productMgmt':    'Produktmanager führen ihre Arbeit als Stakeholder-Management.',
    'communication.csuite':       'Executive Communication ist eine Grundlage der C-Level-Ansprache.',
    'communication.stakeholder':  'Stakeholder-Management erfordert exzellente Kommunikation.',
    'communication.teamLeadership': 'Führung erfordert konsistente Kommunikation.',
    'analytics.sql':              'SQL ist das Arbeitswerkzeug für Datenanalyse.',
    'analytics.hrAnalytics':      'HR Analytics ist eine domänenspezifische Analytics-Spezialisierung.',
    'projectMgmt.agile':          'Agile Delivery ist eine Projektmanagement-Methodik.',
    'projectMgmt.change':         'Veränderungsprogramme erfordern Projektmanagement-Disziplin.',
    'projectMgmt.stakeholder':    'Projektmanager führen Programme als Stakeholder-Management.',
    'change.orgDesign':           'Organisationsdesign ist ein primärer Gegenstand von Change Management.',
    'change.teamLeadership':      'Führung durch Veränderung ist eine Change-Management-Aktivität.',
    'change.communication':       'Change Management hängt von exzellenter Kommunikation ab.',

    // Engineering
    'systemDesign.aws':           'Cloud-Architektur-Arbeit ist eine Kerndomäne von System Design.',
    'systemDesign.k8s':           'K8s-Expertise zeigt verteiltes-Systeme-Design-Verständnis an.',
    'systemDesign.docker':        'Container-Expertise überschneidet sich mit Deployment-Architektur-Design.',
    'cicd.docker':                'Docker ist das kanonische CI/CD-Primitiv.',
    'cicd.k8s':                   'K8s-Deployments sind das Standard-CD-Ziel.',
    'codeReview.mentoring':       'Code Review ist ein Schlüsselkanal für Engineering-Mentorship.',
    'mentoring.codeReview':       'Code Review ist ein Mentoring-Kanal für Engineers.',
    'mentoring.enablement':       'Sales Enablement ist Mentoring im Maßstab.',
    'mentoring.teamLeadership':   'Mentoring ist ein Team-Führungs-Verhalten.',
  },
  // FR/ES/IT could be added later, they fall back to EN cleanly
};

/**
 * Get a localised justification for an adjacency. Falls back to the
 * inline EN justification when no translation is available.
 *
 * @param {string} targetSkillId - the required skill
 * @param {string} viaSkillId - the user's actual skill that bridges
 * @param {string} fallback - the EN justification from skillAdjacency.js
 * @param {string} lang - active UI language
 */
export function localiseJustification(targetSkillId, viaSkillId, fallback, lang) {
  if (lang === 'en') return fallback;
  const key = `${targetSkillId}.${viaSkillId}`;
  return TRANSLATIONS[lang]?.[key] || fallback;
}
